import React from 'react';
import { AiOutlineLogin, AiOutlineLogout } from 'react-icons/ai';
import { ArrowBackIcon, CloseIcon, HamburgerIcon } from '@chakra-ui/icons';
import {
  Avatar,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Tag,
  TagLabel,
  useColorModeValue,
} from '@chakra-ui/react';
import i18next from 'i18next';
import { useAtom } from 'jotai';
import { useRouter } from 'next/router';

import DarkModeToggle from '@/components/0-reusable-components/dark-mode-toggle';
import UserCard from '@/components/0-reusable-components/user-card';
import { meAtom } from '@/store/me.store';
import { colors, zIndexMap } from '@/styles/ui.theme';
import { redirectToSignOut } from '@/utils/services/accounts';

import DescomplicaIcon from '../../../../../assets/svg/descomplica-icon.svg';

type CurrentProps = {
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
  showBackButton: boolean;
};

type BackButtonProps = {
  showBackButton: boolean;
};

const BackButton = ({ showBackButton }: BackButtonProps): React.ReactElement | null => {
  const router = useRouter();
  // eslint-disable-next-line unicorn/no-null
  if (!showBackButton) return null;

  return (
    <IconButton
      variant='ghost'
      size='md'
      icon={<ArrowBackIcon />}
      aria-label={i18next.t('navigationComponent.back')}
      onClick={() => {
        router.back();
      }}
    />
  );
};

const LeftItems = ({
  isOpen,
  onClose,
  onOpen,
  showBackButton,
}: CurrentProps): React.ReactElement => (
  <HStack>
    <IconButton
      variant='ghost'
      size='md'
      icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
      aria-label={i18next.t('navigationComponent.openMenu')}
      onClick={isOpen ? onClose : onOpen}
    />
    <BackButton showBackButton={showBackButton} />
  </HStack>
);

const CenterItems = (): React.ReactElement => (
  <HStack spacing={8} alignItems='center'>
    <Box w='32px' h='32px' mr='16px'>
      <DescomplicaIcon />
    </Box>
  </HStack>
);

type UserRoleProps = { label: string };

const UserRole = ({ label }: UserRoleProps): React.ReactElement => (
  <Tag size='sm' colorScheme='descomplica' borderRadius='full'>
    <TagLabel>{label}</TagLabel>
  </Tag>
);

const RightItems = (): React.ReactElement => {
  const [me] = useAtom(meAtom);

  if (!me) {
    return (
      <Flex alignItems='center'>
        <Menu>
          <DarkModeToggle />
          <MenuItem
            icon={<AiOutlineLogin />}
            onClick={() => redirectToSignOut()}>
            {i18next.t('navigationComponent.signout')}
          </MenuItem>
        </Menu>
      </Flex>
    );
  }

  return (
    <Flex alignItems='center'>
      <Menu>
        <DarkModeToggle />
        <MenuButton as={Button} rounded='full' variant='link' cursor='pointer'>
          <Avatar size='sm' name={me.name} src={me.picture} />
        </MenuButton>
        <MenuList>
          <MenuItem>
            <UserCard name={me.name} email={me.email} imgUrl={me.picture} />
          </MenuItem>
          <MenuItem>
            {me.roles.map((role) => (
              <UserRole key={role.name} label={role.name} />
            ))}
          </MenuItem>
          <MenuDivider />
          <MenuItem icon={<AiOutlineLogout />} onClick={() => redirectToSignOut()}>
            {i18next.t('signout')}
          </MenuItem>
        </MenuList>
      </Menu>
    </Flex>
  );
};

const NavBar = ({ showBackButton, isOpen, onClose, onOpen }: CurrentProps): React.ReactElement => {
  return (
    <Box
      position='sticky'
      top={0}
      zIndex={zIndexMap.hill}
      bg={useColorModeValue('white', 'gray.900')}
      px={4}
      borderBottom={`1px solid ${colors.descomplica.gray['secondary-gray-30']}`}
    >
      <Flex h={16} alignItems='center' justifyContent='space-between'>
        <LeftItems
          isOpen={isOpen}
          onClose={onClose}
          onOpen={onOpen}
          showBackButton={showBackButton}
        />
        <CenterItems />
        <RightItems />
      </Flex>
    </Box>
  );
};

export default NavBar;
